import {
  readMoneyV2,
  readRateV2,
  readSignedMoneyV2,
  type LegacyRateUnrepresentable,
  type MoneyV2,
  type MoneyV2ErrorCode,
  type MoneyV2Read,
  type RateV2,
  type SignedMoneyV2,
} from "./money-v2";

// Readers for the money contract 2 projection blocks a v2 response carries
// next to its legacy objects (contracts/currency/money_v2.schema.json:
// invoice_money, totals_money_v2, estimate_money_v2, history_money_v2,
// settlement_money_v2, payment_money_v2). Every amount and rate goes through the value readers
// of money-v2.ts; this module only adds what a block guarantees about its
// values: one currency and exponent, role signs (MV-4), invoice sums (MV-5)
// and group/completeness rules (MV-6).
//
// A block reads as "unavailable" when it is null or absent and as "invalid"
// when it is present but breaks the contract, so a consumer has no path from
// a broken v2 block to the legacy money fields beside it. Nothing here adds
// groups, converts currencies, infers a missing amount from another one or
// decides whether something is payable.
//
// As with the value readers, keys the contract does not define are ignored:
// the closed schema objects are the server's rule when writing. The keys the
// contract forbids on totals (a cross-currency total) are refused, and the
// payment block is read as closed: it has no header, so its member set is the
// only thing that tells it apart from another block.

export type ChargeRole = "activation" | "energy" | "charging_time" | "idle" | "session_fee" | "tax" | "minimum_charge_adjustment";
export type AdjustmentRole = "discount" | "continuation_suppression" | "correction";
export type MoneyBasis = "captured" | "billed" | "estimate";
export type EstimateStatus = "priced" | "free" | "unavailable" | "unsupported";
export type BillingSource = "persisted" | "legacy_recomputed" | "pending" | "failed" | "unavailable";
export type EstimateComponent = "activation" | "energy" | "charging_time" | "idle" | "minimum_charge_adjustment";
export type PaymentMember = "billed" | "captured" | "amount_due" | "authorized_hold" | "outstanding";

export const CHARGE_ROLES: readonly ChargeRole[] = ["activation", "energy", "charging_time", "idle", "session_fee", "tax", "minimum_charge_adjustment"];
export const ADJUSTMENT_ROLES: readonly AdjustmentRole[] = ["discount", "continuation_suppression", "correction"];
export const MONEY_BASES: readonly MoneyBasis[] = ["captured", "billed", "estimate"];
export const ESTIMATE_STATUSES: readonly EstimateStatus[] = ["priced", "free", "unavailable", "unsupported"];
export const BILLING_SOURCES: readonly BillingSource[] = ["persisted", "legacy_recomputed", "pending", "failed", "unavailable"];
export const ESTIMATE_COMPONENTS: readonly EstimateComponent[] = ["activation", "energy", "charging_time", "idle", "minimum_charge_adjustment"];
export const PAYMENT_MEMBERS: readonly PaymentMember[] = ["billed", "captured", "amount_due", "authorized_hold", "outstanding"];

/** Adjustment roles that only reduce an invoice (MV-4); a correction is signed either way. */
const NON_POSITIVE_ROLES: readonly AdjustmentRole[] = ["discount", "continuation_suppression"];
const ROLES: readonly (ChargeRole | AdjustmentRole)[] = [...CHARGE_ROLES, ...ADJUSTMENT_ROLES];
const FORBIDDEN_TOTAL_KEYS = ["total", "total_minor", "grand_total"];
const COMPLETENESS_COUNTERS = ["unknown_currency_rows", "conflict_rows", "invalid_rows", "unresolved_billing_rows"] as const;
const QUANTITY = /^(0|[1-9][0-9]*)(\.[0-9]*[1-9])?$/;

export interface InvoiceChargeLineV2 {
  readonly kind: "charge";
  readonly role: ChargeRole;
  readonly amount: MoneyV2;
  /** null when the line has no rate. */
  readonly rate: RateV2 | LegacyRateUnrepresentable | null;
  /** Exact decimal string; null when absent. */
  readonly quantity: string | null;
  readonly label_key: string | null;
}

export interface InvoiceAdjustmentLineV2 {
  readonly kind: "adjustment";
  readonly role: AdjustmentRole;
  /** <= 0 for discount and continuation_suppression; either sign for correction. */
  readonly amount: SignedMoneyV2;
  readonly rate: RateV2 | LegacyRateUnrepresentable | null;
  readonly quantity: string | null;
  readonly label_key: string | null;
}

export type InvoiceLineV2 = InvoiceChargeLineV2 | InvoiceAdjustmentLineV2;

/** Finalized v2 invoice: calculated_total = sum(lines), billed_total = calculated_total + capture_adjustment. */
export interface InvoiceMoneyV2 {
  readonly money_version: 2;
  readonly currency: string;
  readonly exponent: number;
  readonly lines: readonly InvoiceLineV2[];
  readonly calculated_total: MoneyV2;
  readonly capture_adjustment: SignedMoneyV2;
  readonly billed_total: MoneyV2;
}

/** One server group: a single basis and currency, never added to another group. */
export interface MoneyGroupV2 {
  readonly basis: MoneyBasis;
  readonly money: MoneyV2;
  readonly rows: number;
}

/** Rows excluded from every group; complete is true exactly when every counter is 0. */
export interface CompletenessV2 {
  readonly complete: boolean;
  readonly unknown_currency_rows: number;
  readonly conflict_rows: number;
  readonly invalid_rows: number;
  readonly unresolved_billing_rows: number;
}

/** Totals in server order. There is deliberately no grand total. */
export interface TotalsMoneyV2 {
  readonly groups: readonly MoneyGroupV2[];
  readonly completeness: CompletenessV2;
}

/** A live-estimate costs.money_v2; null amounts are unknown, never 0. */
export interface EstimateMoneyV2 {
  readonly currency: string;
  readonly exponent: number;
  readonly estimate_status: EstimateStatus;
  readonly total: MoneyV2 | null;
  /** The five contract components plus any further component the server sends. */
  readonly components: Readonly<Record<EstimateComponent, MoneyV2 | null>> & Readonly<Record<string, MoneyV2 | null>>;
}

/** A session_history[].costs.money_v2. */
export interface HistoryMoneyV2 {
  readonly currency: string;
  readonly exponent: number;
  readonly total: MoneyV2 | null;
  readonly billing_source: BillingSource;
  /** null when the block carries no invoice. */
  readonly invoice: InvoiceMoneyV2 | null;
}

/** The public settlement.money_v2: amount due, charged and outstanding stay separate; null is unknown. */
export interface SettlementMoneyV2 {
  readonly currency: string;
  readonly exponent: number;
  readonly invoice_total: MoneyV2 | null;
  readonly charged: MoneyV2 | null;
  readonly outstanding: MoneyV2 | null;
}

/**
 * A session_history[].payment.money_v2: five separate amounts of one payment
 * record, null is unknown. There is no block currency, so an all-null block
 * names none; outstanding_known lives beside the block and is not read here.
 */
export type PaymentMoneyV2 = Readonly<Record<PaymentMember, MoneyV2 | null>>;

export type ProjectionErrorCode =
  | MoneyV2ErrorCode
  | "not_array"
  | "enum"
  | "currency_mismatch"
  | "exponent_mismatch"
  | "sign"
  | "line_sum"
  | "billed_sum"
  | "quantity"
  | "label_key"
  | "counter"
  | "completeness"
  | "duplicate_group"
  | "forbidden_key";

/** Like MoneyV2Read; path locates the offending field inside the block ("lines[2].amount"). */
export type ProjectionRead<T> =
  | { readonly status: "value"; readonly value: T }
  | { readonly status: "unavailable" }
  | { readonly status: "invalid"; readonly error: ProjectionErrorCode; readonly path: string; readonly detail: string };

class Invalid extends Error {
  readonly error: ProjectionErrorCode;
  readonly path: string;
  readonly detail: string;

  constructor(error: ProjectionErrorCode, path: string, detail: string) {
    super(`${path}: ${error}: ${detail}`);
    this.error = error;
    this.path = path;
    this.detail = detail;
  }
}

const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : String(value));
const join = (path: string, key: string) => (path ? `${path}.${key}` : key);
const has = (record: Record<string, unknown>, key: string) => Object.prototype.hasOwnProperty.call(record, key);

function object(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Invalid("not_object", path, show(value));
  return value as Record<string, unknown>;
}

function field(record: Record<string, unknown>, key: string, path: string): unknown {
  if (!has(record, key)) throw new Invalid("missing_field", join(path, key), key);
  return record[key];
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (!allowed.includes(value as T)) throw new Invalid("enum", path, show(value));
  return value as T;
}

function value<T>(result: MoneyV2Read<T>, path: string): T | null {
  if (result.status === "invalid") throw new Invalid(result.error, path, result.detail);
  return result.status === "value" ? result.value : null;
}

interface Header {
  readonly currency: string;
  readonly exponent: number;
}

// A block's currency and exponent are valid exactly when a zero amount in
// them is, so the value reader's own rules apply without a second copy.
function header(record: Record<string, unknown>, path: string): Header {
  const currency = field(record, "currency", path);
  const exponent = field(record, "exponent", path);
  const probe = readMoneyV2({ currency, amount_minor: "0", exponent, money_version: 2 });
  if (probe.status !== "value") {
    const key = probe.status === "invalid" && probe.error === "exponent" ? "exponent" : "currency";
    throw new Invalid(probe.status === "invalid" ? probe.error : "currency", join(path, key), show(record[key]));
  }
  return { currency: probe.value.currency, exponent: probe.value.exponent };
}

function agree<T extends MoneyV2 | SignedMoneyV2 | null>(amount: T, block: Header, path: string): T {
  if (amount === null) return amount;
  if (amount.currency !== block.currency) throw new Invalid("currency_mismatch", join(path, "currency"), `${amount.currency} in a ${block.currency} block`);
  if (amount.exponent !== block.exponent) throw new Invalid("exponent_mismatch", join(path, "exponent"), `${amount.exponent} in a block with exponent ${block.exponent}`);
  return amount;
}

/** A required money_or_unknown member: absent is a broken block, null is unknown. */
function moneyOrUnknown(record: Record<string, unknown>, key: string, block: Header, path: string): MoneyV2 | null {
  const at = join(path, key);
  return agree(value(readMoneyV2(field(record, key, path)), at), block, at);
}

function money(record: Record<string, unknown>, key: string, block: Header, path: string, signed = false): MoneyV2 {
  const at = join(path, key);
  const raw = field(record, key, path);
  const amount = value(signed ? readSignedMoneyV2(raw) : readMoneyV2(raw), at);
  if (amount === null) throw new Invalid("not_object", at, "null");
  return agree(amount, block, at);
}

function block<T>(input: unknown, parse: (record: Record<string, unknown>) => T): ProjectionRead<T> {
  if (input === null || input === undefined) return { status: "unavailable" };
  try {
    return { status: "value", value: deepFreeze(parse(object(input, ""))) };
  } catch (e) {
    if (e instanceof Invalid) return { status: "invalid", error: e.error, path: e.path, detail: e.detail };
    throw e;
  }
}

function deepFreeze<T>(input: T): T {
  if (typeof input === "object" && input !== null && !Object.isFrozen(input)) {
    for (const member of Object.values(input)) deepFreeze(member);
    Object.freeze(input);
  }
  return input;
}

function line(input: unknown, block: Header, path: string): InvoiceLineV2 {
  const record = object(input, path);
  const role = oneOf(field(record, "role", path), ROLES, join(path, "role"));
  const adjustment = ADJUSTMENT_ROLES.includes(role as AdjustmentRole);
  const amount = money(record, "amount", block, path, adjustment);
  if (NON_POSITIVE_ROLES.includes(role as AdjustmentRole) && BigInt(amount.amount_minor) > 0n) throw new Invalid("sign", join(path, "amount.amount_minor"), `${role} ${amount.amount_minor} > 0`);

  let rate: RateV2 | LegacyRateUnrepresentable | null = null;
  if (has(record, "rate")) {
    const at = join(path, "rate");
    rate = value(readRateV2(record.rate), at);
    if (rate === null) throw new Invalid("not_object", at, "null");
    if (!("diagnostic" in rate) && rate.currency !== block.currency) throw new Invalid("currency_mismatch", join(at, "currency"), `${rate.currency} in a ${block.currency} block`);
  }
  let quantity: string | null = null;
  if (has(record, "quantity")) {
    if (typeof record.quantity !== "string" || !QUANTITY.test(record.quantity)) throw new Invalid("quantity", join(path, "quantity"), show(record.quantity));
    quantity = record.quantity;
  }
  let label_key: string | null = null;
  if (has(record, "label_key")) {
    if (typeof record.label_key !== "string") throw new Invalid("label_key", join(path, "label_key"), show(record.label_key));
    label_key = record.label_key;
  }
  return adjustment
    ? { kind: "adjustment", role: role as AdjustmentRole, amount, rate, quantity, label_key }
    : { kind: "charge", role: role as ChargeRole, amount, rate, quantity, label_key };
}

function invoice(record: Record<string, unknown>, path: string): InvoiceMoneyV2 {
  const version = field(record, "money_version", path);
  if (version !== 2) throw new Invalid("money_version", join(path, "money_version"), show(version));
  const head = header(record, path);
  const rawLines = field(record, "lines", path);
  if (!Array.isArray(rawLines)) throw new Invalid("not_array", join(path, "lines"), show(rawLines));
  const lines = rawLines.map((entry, i) => line(entry, head, `${join(path, "lines")}[${i}]`));
  const calculated_total = money(record, "calculated_total", head, path);
  const capture_adjustment = money(record, "capture_adjustment", head, path, true);
  const billed_total = money(record, "billed_total", head, path);

  // bigint sums cannot overflow: an intermediate beyond int64 is fine as long as the totals the server sent match.
  const sum = lines.reduce((total, l) => total + BigInt(l.amount.amount_minor), 0n);
  if (sum !== BigInt(calculated_total.amount_minor)) throw new Invalid("line_sum", join(path, "calculated_total"), `lines sum to ${sum}, calculated_total is ${calculated_total.amount_minor}`);
  const billed = BigInt(calculated_total.amount_minor) + BigInt(capture_adjustment.amount_minor);
  if (billed !== BigInt(billed_total.amount_minor)) throw new Invalid("billed_sum", join(path, "billed_total"), `calculated_total + capture_adjustment is ${billed}, billed_total is ${billed_total.amount_minor}`);

  return { money_version: 2, currency: head.currency, exponent: head.exponent, lines, calculated_total, capture_adjustment, billed_total };
}

function counter(record: Record<string, unknown>, key: string, path: string): number {
  const raw = field(record, key, path);
  if (typeof raw !== "number" || !Number.isSafeInteger(raw) || raw < 0) throw new Invalid("counter", join(path, key), show(raw));
  return raw;
}

function group(input: unknown, path: string): MoneyGroupV2 {
  const record = object(input, path);
  const basis = oneOf(field(record, "basis", path), MONEY_BASES, join(path, "basis"));
  // The group carries the four value fields itself.
  const amount = value(readMoneyV2(record), path);
  if (amount === null) throw new Invalid("not_object", path, "null");
  return { basis, money: amount, rows: counter(record, "rows", path) };
}

function completeness(input: unknown, path: string): CompletenessV2 {
  const record = object(input, path);
  const complete = field(record, "complete", path);
  if (typeof complete !== "boolean") throw new Invalid("completeness", join(path, "complete"), show(complete));
  const counters = Object.fromEntries(COMPLETENESS_COUNTERS.map((key) => [key, counter(record, key, path)])) as Record<(typeof COMPLETENESS_COUNTERS)[number], number>;
  const allZero = COMPLETENESS_COUNTERS.every((key) => counters[key] === 0);
  if (complete !== allZero) throw new Invalid("completeness", join(path, "complete"), `complete ${complete} with counters ${JSON.stringify(counters)}`);
  return { complete, ...counters };
}

/** Reads a finalized invoice's money block (`invoice_money`): role signs, one currency/exponent and both sums checked. */
export function readInvoiceMoneyV2(input: unknown): ProjectionRead<InvoiceMoneyV2> {
  return block(input, (record) => invoice(record, ""));
}

/**
 * Reads a `totals_money_v2` block. Groups keep the server's order and are
 * never added together; two groups with the same (basis, currency), a
 * cross-currency total key or an inconsistent completeness are invalid (MV-6).
 */
export function readTotalsMoneyV2(input: unknown): ProjectionRead<TotalsMoneyV2> {
  return block(input, (record) => {
    const forbidden = FORBIDDEN_TOTAL_KEYS.find((key) => has(record, key));
    if (forbidden !== undefined) throw new Invalid("forbidden_key", forbidden, "a total across currencies or bases is not part of money contract 2");
    const rawGroups = field(record, "groups", "");
    if (!Array.isArray(rawGroups)) throw new Invalid("not_array", "groups", show(rawGroups));
    const groups = rawGroups.map((entry, i) => group(entry, `groups[${i}]`));
    const seen = new Set<string>();
    groups.forEach((g, i) => {
      const key = `${g.basis}/${g.money.currency}`;
      if (seen.has(key)) throw new Invalid("duplicate_group", `groups[${i}]`, key);
      seen.add(key);
    });
    return { groups, completeness: completeness(field(record, "completeness", ""), "completeness") };
  });
}

/** Reads a live-estimate `costs.money_v2` (`estimate_money_v2`); the status is kept as sent. */
export function readEstimateMoneyV2(input: unknown): ProjectionRead<EstimateMoneyV2> {
  return block(input, (record) => {
    const head = header(record, "");
    const estimate_status = oneOf(field(record, "estimate_status", ""), ESTIMATE_STATUSES, "estimate_status");
    const total = moneyOrUnknown(record, "total", head, "");
    const rawComponents = object(field(record, "components", ""), "components");
    for (const key of ESTIMATE_COMPONENTS) field(rawComponents, key, "components");
    const components = Object.fromEntries(Object.keys(rawComponents).map((key) => [key, moneyOrUnknown(rawComponents, key, head, "components")]));
    return { currency: head.currency, exponent: head.exponent, estimate_status, total, components: components as EstimateMoneyV2["components"] };
  });
}

/** Reads a `session_history[].costs.money_v2` (`history_money_v2`), including its optional invoice. */
export function readHistoryMoneyV2(input: unknown): ProjectionRead<HistoryMoneyV2> {
  return block(input, (record) => {
    const head = header(record, "");
    const total = moneyOrUnknown(record, "total", head, "");
    const billing_source = oneOf(field(record, "billing_source", ""), BILLING_SOURCES, "billing_source");
    let parsed: InvoiceMoneyV2 | null = null;
    if (has(record, "invoice")) {
      parsed = invoice(object(record.invoice, "invoice"), "invoice");
      if (parsed.currency !== head.currency) throw new Invalid("currency_mismatch", "invoice.currency", `${parsed.currency} in a ${head.currency} block`);
      if (parsed.exponent !== head.exponent) throw new Invalid("exponent_mismatch", "invoice.exponent", `${parsed.exponent} in a block with exponent ${head.exponent}`);
    }
    return { currency: head.currency, exponent: head.exponent, total, billing_source, invoice: parsed };
  });
}

/** Reads the public `settlement.money_v2` (`settlement_money_v2`); no amount is derived from another. */
export function readSettlementMoneyV2(input: unknown): ProjectionRead<SettlementMoneyV2> {
  return block(input, (record) => {
    const head = header(record, "");
    return {
      currency: head.currency,
      exponent: head.exponent,
      invoice_total: moneyOrUnknown(record, "invoice_total", head, ""),
      charged: moneyOrUnknown(record, "charged", head, ""),
      outstanding: moneyOrUnknown(record, "outstanding", head, ""),
    };
  });
}

/**
 * Reads a `session_history[].payment.money_v2` (`payment_money_v2`): exactly
 * the five members, the non-null ones in one currency and exponent. Nothing is
 * summed or derived; the sibling outstanding_known rule belongs to the reader
 * of the payment object.
 */
export function readPaymentMoneyV2(input: unknown): ProjectionRead<PaymentMoneyV2> {
  return block(input, (record) => {
    const prototype = Object.getPrototypeOf(record);
    if (prototype !== Object.prototype && prototype !== null) throw new Invalid("not_object", "", "not a plain object");
    for (const key of PAYMENT_MEMBERS) field(record, key, "");
    const extra = Object.keys(record).find((key) => !PAYMENT_MEMBERS.includes(key as PaymentMember));
    if (extra !== undefined) throw new Invalid("forbidden_key", extra, "not a payment_money_v2 member");
    let head: Header | null = null;
    const members: Partial<Record<PaymentMember, MoneyV2 | null>> = {};
    for (const key of PAYMENT_MEMBERS) {
      const amount = value(readMoneyV2(record[key]), key);
      if (amount !== null) head ??= amount;
      members[key] = head === null ? amount : agree(amount, head, key);
    }
    return members as PaymentMoneyV2;
  });
}
